/**
 * ContractsPage — ★ Fase 8: sezione Contratti.
 * Tab «Inviati» (amministrazione + Senior) e «Matrici» (solo amministrazione).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  FileSignature, Plus, RefreshCw, Pencil, Ban, ShieldQuestion, Lock, CopyPlus,
  Archive, FileDown, Search, Loader2, Paperclip, Stamp,
} from 'lucide-react';
import { SendContractDialog } from '../components/contracts/SendContractDialog';
import { ContractReviewDialog } from '../components/contracts/ContractReviewDialog';
// ★ Fase 14 — impostazioni firma + timbro per la controfirma aziendale
import { CountersignSettingsDialog } from '../components/contracts/CountersignSettingsDialog';
import { STATUS_META, AI_VERDICT_META, fmtDT } from '../components/contracts/contractUtils';

export default function ContractsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [perms, setPerms] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [reviewId, setReviewId] = useState(null);
  // ★ Fase 14 — dialog impostazioni controfirma
  const [csSettingsOpen, setCsSettingsOpen] = useState(false);
  // ★ Fase 8.Y — filtri Archivio firmati
  const [archSearch, setArchSearch] = useState('');
  const [archFrom, setArchFrom] = useState('');
  const [archTo, setArchTo] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  const loadContracts = useCallback(() => {
    api.get('/contracts').then((r) => setContracts(r.data || [])).catch(() => setContracts([]));
  }, []);
  const loadTemplates = useCallback(() => {
    api.get('/contracts/templates').then((r) => setTemplates(r.data || [])).catch(() => setTemplates([]));
  }, []);

  useEffect(() => {
    api.get('/contracts/permissions')
      .then((r) => {
        setPerms(r.data);
        if (r.data.can_send) { loadContracts(); loadTemplates(); }
      })
      .catch(() => setPerms({ can_send: false, can_manage_templates: false }));
  }, [loadContracts, loadTemplates]);

  const disableTemplate = async (t2) => {
    if (!window.confirm(t('contractsPage.disableConfirm', { defaultValue: 'Disattivare la matrice «{{name}}»?', name: t2.name }))) return;
    try {
      await api.delete(`/contracts/templates/${t2.id}`);
      toast.success(t('contractsPage.templateDisabled', 'Matrice disattivata'));
      loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('contractsPage.genericError', 'Errore'));
    }
  };

  // ★ Fase 8.X — duplica la matrice e apre subito l'editor della copia
  const duplicateTemplate = async (t2) => {
    try {
      const { data } = await api.post(`/contracts/templates/${t2.id}/duplicate`);
      toast.success(t('contractsPage.templateDuplicated', { defaultValue: 'Matrice duplicata: «{{name}}»', name: data.name }), {
        description: t('contractsPage.templateDuplicatedDesc', 'Si apre l\u2019editor della copia: modificala e salva.'),
      });
      navigate(`/app/contracts/matrice/${data.template_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('contractsPage.duplicateError', 'Errore duplicazione matrice'));
    }
  };

  // ★ Fase 8.Y — Archivio: contratti firmati/approvati con filtri cliente + data
  const archived = useMemo(() => {
    const q = archSearch.trim().toLowerCase();
    return (contracts || [])
      .filter((c) => ['signed', 'approved'].includes(c.status))
      .filter((c) => {
        if (!q) return true;
        return [c.recipient_name, c.recipient_email, c.template_name]
          .some((v) => (v || '').toLowerCase().includes(q));
      })
      .filter((c) => {
        const signedAt = c.signature?.signed_at || c.updated_at || '';
        const day = signedAt.slice(0, 10);
        if (archFrom && day < archFrom) return false;
        if (archTo && day > archTo) return false;
        return true;
      })
      .sort((a, b) => (b.signature?.signed_at || '').localeCompare(a.signature?.signed_at || ''));
  }, [contracts, archSearch, archFrom, archTo]);

  const downloadPdf = async (c, e) => {
    e?.stopPropagation();
    setDownloadingId(c.id);
    try {
      const r = await api.get(`/contracts/${c.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(c.template_name || 'contratto').replace(/[^\w-]+/g, '_')}_${c.recipient_name.replace(/[^\w-]+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error(t('contractsPage.pdfUnavailable', 'PDF non disponibile per questo contratto'));
    } finally {
      setDownloadingId(null);
    }
  };

  if (perms && !perms.can_send) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><CardContent className="p-8 text-center">
          <Lock className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
          <div className="font-medium">{t('contractsPage.restrictedSection', 'Sezione riservata')}</div>
          <p className="text-sm text-muted-foreground mt-1" data-testid="contracts-denied">
            {t('contractsPage.restrictedDesc', 'L\u2019invio dei contratti è riservato all\u2019amministrazione e ai Senior.')}
          </p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="contracts-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl text-[hsl(var(--primary))] flex items-center gap-2">
            <FileSignature className="w-7 h-7" /> {t('contractsPage.title', 'Contratti')}
          </h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">
            {t('contractsPage.subtitle', 'Firma elettronica con OTP, documento + selfie e verifica finale dell\u2019amministrazione.')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* ★ Fase 14 — impostazioni firma/timbro per la controfirma */}
          {perms?.can_manage_templates && (
            <Button
              variant="outline"
              onClick={() => setCsSettingsOpen(true)}
              data-testid="countersign-settings-btn"
            >
              <Stamp className="w-4 h-4 mr-1.5" /> {t('contractsPage.companySignature', 'Firma aziendale')}
            </Button>
          )}
          <Button onClick={() => setSendOpen(true)} data-testid="contracts-new-btn" className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]">
            <Plus className="w-4 h-4 mr-1.5" /> {t('contractsPage.newContract', 'Nuovo contratto')}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="sent">
        <TabsList>
          <TabsTrigger value="sent" data-testid="contracts-tab-sent">{t('contractsPage.tabSent', 'Inviati')}</TabsTrigger>
          <TabsTrigger value="archive" data-testid="contracts-tab-archive">
            {t('contractsPage.tabArchive', 'Archivio firmati')}{archived.length > 0 ? ` (${archived.length})` : ''}
          </TabsTrigger>
          {perms?.can_send && (
            <TabsTrigger value="templates" data-testid="contracts-tab-templates">{t('contractsPage.tabTemplates', 'Matrici')}</TabsTrigger>
          )}
        </TabsList>

        {/* ── INVIATI ── */}
        <TabsContent value="sent" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="text-sm font-medium">{contracts?.length ?? '…'} {t('contractsPage.contracts', 'contratti')}</span>
                <Button size="sm" variant="ghost" className="h-8" onClick={loadContracts} data-testid="contracts-refresh">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              {contracts === null ? (
                <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}</div>
              ) : contracts.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground" data-testid="contracts-empty">
                  {t('contractsPage.emptySent', 'Nessun contratto inviato. Premi «Nuovo contratto» per iniziare.')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('contractsPage.thSerial', 'N. serie')}</TableHead>
                        <TableHead>{t('contractsPage.thSigner', 'Firmatario')}</TableHead>
                        <TableHead>{t('contractsPage.thTemplate', 'Matrice')}</TableHead>
                        <TableHead>{t('contractsPage.thStatus', 'Stato')}</TableHead>
                        <TableHead>{t('contractsPage.thAiIdentity', 'AI identità')}</TableHead>
                        <TableHead>{t('contractsPage.thSentBy', 'Inviato da')}</TableHead>
                        <TableHead>{t('contractsPage.thSentDate', 'Data invio')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contracts.map((c) => {
                        const st = STATUS_META[c.status] || {};
                        const verdict = c.identity?.ai_check?.same_person;
                        const aiMeta = verdict ? (AI_VERDICT_META[verdict] || AI_VERDICT_META.non_valutabile) : null;
                        return (
                          <TableRow
                            key={c.id}
                            className="cursor-pointer hover:bg-[var(--surface-2)]/60"
                            onClick={() => setReviewId(c.id)}
                            data-testid={`contract-row-${c.id}`}
                          >
                            <TableCell className="text-xs font-mono text-muted-foreground" data-testid={`contract-serial-${c.id}`}>
                              {c.serial_number || '—'}
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-sm">{c.recipient_name}</div>
                              <div className="text-[11px] text-muted-foreground">{c.recipient_email}</div>
                            </TableCell>
                            <TableCell className="text-sm">{c.template_name}</TableCell>
                            <TableCell>
                              <Badge className={`border text-[10px] ${st.cls || ''}`}>{st.label || c.status}</Badge>
                              {c.countersign?.signed_at && (
                                <span className="inline-flex items-center gap-0.5 ml-1.5 text-[10px] text-[#1a3dae]" title={`${t('contractsPage.countersignedOn', 'Controfirmato il')} ${fmtDT(c.countersign.signed_at)}`} data-testid={`contract-countersigned-${c.id}`}>
                                  <Stamp className="w-3 h-3" /> {t('contractsPage.countersigned', 'Controfirmato')}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {aiMeta ? (
                                <span className={`text-xs font-medium inline-flex items-center gap-1 ${aiMeta.cls}`}>
                                  <ShieldQuestion className="w-3.5 h-3.5" /> {aiMeta.label}
                                </span>
                              ) : <span className="text-xs text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell className="text-xs">{c.sent_by_name || c.sent_by}</TableCell>
                            <TableCell className="text-xs tabular-nums">{fmtDT(c.sent_at)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── ★ Fase 8.Y — ARCHIVIO FIRMATI ── */}
        <TabsContent value="archive" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center gap-2 flex-wrap px-4 py-3 border-b border-border">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={archSearch}
                    onChange={(e) => setArchSearch(e.target.value)}
                    placeholder={t('contractsPage.archiveSearch', 'Cerca cliente, email o matrice…')}
                    className="h-9 pl-8 text-sm"
                    data-testid="archive-search"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground">{t('contractsPage.fromDate', 'Dal')}</span>
                  <Input
                    type="date"
                    value={archFrom}
                    onChange={(e) => setArchFrom(e.target.value)}
                    className="h-9 w-[140px] text-sm"
                    data-testid="archive-date-from"
                  />
                  <span className="text-[11px] text-muted-foreground">{t('contractsPage.toDate', 'al')}</span>
                  <Input
                    type="date"
                    value={archTo}
                    onChange={(e) => setArchTo(e.target.value)}
                    className="h-9 w-[140px] text-sm"
                    data-testid="archive-date-to"
                  />
                </div>
                {(archSearch || archFrom || archTo) && (
                  <Button
                    size="sm" variant="ghost" className="h-9 text-xs"
                    onClick={() => { setArchSearch(''); setArchFrom(''); setArchTo(''); }}
                    data-testid="archive-reset-filters"
                  >
                    {t('contractsPage.resetFilters', 'Azzera filtri')}
                  </Button>
                )}
              </div>
              {contracts === null ? (
                <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}</div>
              ) : archived.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground" data-testid="archive-empty">
                  <Archive className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                  {archSearch || archFrom || archTo
                    ? t('contractsPage.archiveEmptyFiltered', 'Nessun contratto firmato corrisponde ai filtri.')
                    : t('contractsPage.archiveEmpty', 'Nessun contratto firmato o approvato al momento.')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('contractsPage.thSerial', 'N. serie')}</TableHead>
                        <TableHead>{t('contractsPage.thClient', 'Cliente')}</TableHead>
                        <TableHead>{t('contractsPage.thTemplate', 'Matrice')}</TableHead>
                        <TableHead>{t('contractsPage.thSignedOn', 'Firmato il')}</TableHead>
                        <TableHead>{t('contractsPage.thStatus', 'Stato')}</TableHead>
                        <TableHead className="text-right">PDF</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="archive-list">
                      {archived.map((c) => {
                        const st = STATUS_META[c.status] || {};
                        return (
                          <TableRow
                            key={c.id}
                            className="cursor-pointer hover:bg-[var(--surface-2)]/60"
                            onClick={() => setReviewId(c.id)}
                            data-testid={`archive-row-${c.id}`}
                          >
                            <TableCell className="text-xs font-mono text-muted-foreground">{c.serial_number || '—'}</TableCell>
                            <TableCell>
                              <div className="font-medium text-sm">{c.recipient_name}</div>
                              <div className="text-[11px] text-muted-foreground">{c.recipient_email}</div>
                            </TableCell>
                            <TableCell className="text-sm">{c.template_name}</TableCell>
                            <TableCell className="text-xs tabular-nums">{fmtDT(c.signature?.signed_at)}</TableCell>
                            <TableCell>
                              <Badge className={`border text-[10px] ${st.cls || ''}`}>{st.label || c.status}</Badge>
                              {c.countersign?.signed_at && (
                                <span className="inline-flex items-center gap-0.5 ml-1.5 text-[10px] text-[#1a3dae]" title={`${t('contractsPage.countersignedOn', 'Controfirmato il')} ${fmtDT(c.countersign.signed_at)}`} data-testid={`archive-countersigned-${c.id}`}>
                                  <Stamp className="w-3 h-3" /> {t('contractsPage.countersigned', 'Controfirmato')}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs"
                                onClick={(e) => downloadPdf(c, e)}
                                disabled={downloadingId === c.id}
                                data-testid={`archive-pdf-${c.id}`}
                              >
                                {downloadingId === c.id
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <FileDown className="w-3.5 h-3.5" />}
                                <span className="ml-1">{t('contractsPage.download', 'Scarica')}</span>
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── MATRICI ── */}
        {/* ★ Fase 24 — visibile anche alla Direttrice Commerciale (can_send):
            lista in sola lettura; creazione/modifica restano admin-only. */}
        {perms?.can_send && (
          <TabsContent value="templates" className="mt-4 space-y-3">
            {perms?.can_manage_templates ? (
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => navigate('/app/contracts/matrice/nuova')} data-testid="tpl-new-btn">
                  <Plus className="w-4 h-4 mr-1" /> {t('contractsPage.newTemplate', 'Nuova matrice')}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="tpl-readonly-hint">
                {t('contractsPage.readonlyHint', 'Matrici disponibili per l\u2019invio (sola lettura). Usa «Nuovo contratto» per inviarne una.')}
              </p>
            )}
            {templates === null ? (
              <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-20" />)}</div>
            ) : templates.length === 0 ? (
              <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="templates-empty">
                {t('contractsPage.templatesEmpty', 'Nessuna matrice. Creane una: potrai riusarla per tutti gli invii.')}
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="templates-list">
                {templates.map((tpl) => (
                  <Card key={tpl.id} className={tpl.active === false ? 'opacity-50' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{tpl.name} {tpl.active === false && <span className="text-[10px] text-red-500">({t('contractsPage.disabled', 'disattivata')})</span>}</div>
                          {tpl.description && <div className="text-xs text-muted-foreground truncate">{tpl.description}</div>}
                          <div className="text-[11px] text-muted-foreground mt-1.5">
                            {(tpl.fields || []).length} {t('contractsPage.fields', 'campi')} · {(tpl.fields || []).map((f) => f.label).join(', ')}
                          </div>
                          {(tpl.attachments || []).length > 0 && (
                            <div
                              className="text-[11px] text-[hsl(var(--primary))] mt-1 inline-flex items-center gap-1"
                              data-testid={`tpl-attachments-badge-${tpl.id}`}
                              title={(tpl.attachments || []).map((a) => a.name).join(', ')}
                            >
                              <Paperclip className="w-3 h-3" />
                              {tpl.attachments.length} {t('contractsPage.defaultAttachments', { defaultValue: 'allegati predefiniti', count: tpl.attachments.length })}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {perms?.can_manage_templates && (
                            <>
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => navigate(`/app/contracts/matrice/${tpl.id}`)} title={t('contractsPage.edit', 'Modifica')} data-testid={`tpl-edit-${tpl.id}`}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => duplicateTemplate(tpl)} title={t('contractsPage.duplicate', 'Duplica matrice')} data-testid={`tpl-duplicate-${tpl.id}`}>
                                <CopyPlus className="w-3.5 h-3.5" />
                              </Button>
                              {tpl.active !== false && (
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => disableTemplate(tpl)} data-testid={`tpl-disable-${tpl.id}`}>
                                  <Ban className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      <SendContractDialog open={sendOpen} onClose={() => setSendOpen(false)} onSent={loadContracts} />
      <CountersignSettingsDialog open={csSettingsOpen} onClose={() => setCsSettingsOpen(false)} />
      <ContractReviewDialog
        contractId={reviewId}
        open={!!reviewId}
        onClose={() => setReviewId(null)}
        onChanged={loadContracts}
        canManage={!!perms?.can_manage_templates}
      />
    </div>
  );
}
